import { zodResolver } from '@hookform/resolvers/zod';
import {
  queryOptions,
  useMutation,
  useSuspenseQuery,
  type InfiniteData,
} from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { LoaderCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { Form } from '@components/form';
import { InputControlled } from '@components/input/controlled';
import { PageTitle } from '@components/PageTitle';
import { Anchor } from '@components/ui/anchor';
import { Button } from '@components/ui/button';
import { Separator } from '@components/ui/separator';
import { interventionVatTypeOptions } from '@constants/interventions.constant';
import { queryKeys } from '@constants/queryKeys.constant';
import { satisfactionOptions } from '@constants/satisfactions.constant';
import { EServiceType, EServiceWorkflow } from '@enums/service.enum';
import { ESmartEventTimelineKeyType } from '@enums/smartEvent.enum';
import { EStaleTime } from '@enums/staleTime.enum';
import { formatCentsToString } from '@formatters/currency.formatter';
import { formatDate } from '@formatters/date.formatter';
import { formatFullName } from '@formatters/fullName.formatter';
import { getInterventionFieldVisibility } from '@helpers/getInterventionFieldVisibility.helper';
import { handleRequestError } from '@helpers/handleRequestError.helper';
import { invalidateSmartEventTimeline } from '@helpers/queryCache/invalidateSmartEventTimeline.helper';
import type { Intervention } from '@models/intervention.model';
import {
  updateInterventionSchema,
  type UpdateInterventionDto,
  type UpdateInterventionFormInput,
  type UpdateInterventionPayload,
} from '@services/interventions/dtos/update.dto';
import { interventionService } from '@services/interventions/service';
import type { UpdateInterventionOutput } from '@services/interventions/types/update.type';
import type { GetAllServiceRequestInterventionsOutput } from '@services/serviceRequests/types/getAllInterventions.type';

import { InterventionScheduleFields } from '../@components/ScheduleFields';

function toDatePayload({
  isVisible,
  next,
  previous,
}: {
  isVisible: boolean;
  next?: string | null;
  previous?: string | null;
}) {
  if (!isVisible) {
    return undefined;
  }

  if (previous && !next) {
    return null;
  }

  return next;
}

function interventionQueryOptions(id: string) {
  return queryOptions({
    queryKey: queryKeys.interventions.detail(id),
    queryFn: () => interventionService.getById(id),
    staleTime: EStaleTime.OneHour,
  });
}

export const Route = createFileRoute(
  '/_layout/(private)/service-requests/$id/interventions/$interventionId/update',
)({
  loader: ({ context, params }) => {
    const options = interventionQueryOptions(params.interventionId);
    return context.queryClient.ensureQueryData({
      ...options,
      revalidateIfStale: true,
    });
  },
  pendingComponent: () => (
    <div className="flex min-h-dvh w-full items-center justify-center">
      <LoaderCircle className="text-primary animate-spin" size={80} />
    </div>
  ),
  pendingMs: 300,
  pendingMinMs: 600,
  component: InterventionUpdate,
});

function InterventionUpdate() {
  const { queryClient } = Route.useRouteContext();
  const { id: serviceRequestId, interventionId } = Route.useParams();

  const navigate = Route.useNavigate();

  const { data } = useSuspenseQuery(interventionQueryOptions(interventionId));
  const isQuotedWork =
    data.intervention.serviceType?.name === EServiceType.QuotedWork;
  const invoiceVatWorkflows: Array<string> = [
    EServiceWorkflow.DirectWorkStandard,
    EServiceWorkflow.InspectionStandard,
    EServiceWorkflow.QuotedWorkStandard,
  ];
  const hasInvoiceVatFields = invoiceVatWorkflows.includes(
    data.intervention.serviceWorkflow?.name ?? '',
  );
  const {
    hasInvoiceAmount: hasServiceInvoiceAmount,
    hasQuoteAmount,
    hasWorkDates,
  } = getInterventionFieldVisibility({
    serviceType: data.intervention.serviceType?.name,
    serviceWorkflow: data.intervention.serviceWorkflow?.name,
  });
  const hasVisitFields =
    data.intervention.serviceType?.name !== EServiceType.DirectWork &&
    data.intervention.serviceWorkflow?.name !==
      EServiceWorkflow.DirectWorkStandard;
  const hasInvoiceAmount =
    hasServiceInvoiceAmount &&
    data.intervention.serviceWorkflow?.name !==
      EServiceWorkflow.DirectWorkWithoutInvoice;

  const form = useForm<
    UpdateInterventionFormInput,
    unknown,
    UpdateInterventionDto
  >({
    resolver: zodResolver(updateInterventionSchema),
    mode: 'onBlur',
    shouldUnregister: true,
    defaultValues: {
      visitDate: formatDate(
        hasVisitFields ? data.intervention.visitDate : undefined,
        { format: 'YYYY-MM-DD' },
      ),
      workDate: formatDate(data.intervention.workDate, {
        format: 'YYYY-MM-DD',
      }),
      workEndDate: formatDate(data.intervention.workEndDate, {
        format: 'YYYY-MM-DD',
      }),
      remark: data.intervention.remark,
      invoiceAmount: formatCentsToString(data.intervention.invoiceAmountCents),
      invoiceAmountExclVat: formatCentsToString(
        data.intervention.invoiceAmountExclVatCents,
      ),
      invoiceVatType: data.intervention.invoiceVatType ?? '',
      quoteAmount: formatCentsToString(data.intervention.quoteAmountCents),
      quoteAmountExclVat: formatCentsToString(
        data.intervention.quoteAmountExclVatCents,
      ),
      quoteVatType: data.intervention.quoteVatType ?? '',
      satisfaction: data.intervention.satisfaction,
    },
  });

  const { isPending, mutate: updateIntervention } = useMutation<
    UpdateInterventionOutput,
    unknown,
    UpdateInterventionPayload
  >({
    mutationFn: interventionService.update,
    onError: handleRequestError,
    onSuccess: async ({ intervention }) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.serviceRequests.interventions(serviceRequestId),
      });

      queryClient.setQueriesData<
        InfiniteData<GetAllServiceRequestInterventionsOutput>
      >(
        {
          queryKey: queryKeys.serviceRequests.interventions(serviceRequestId),
        },
        oldData => {
          if (!oldData) {
            return oldData;
          }

          return {
            ...oldData,
            pages: oldData.pages.map(page => ({
              ...page,
              list: page.list.map(item => {
                if (item.id === intervention.id) {
                  const updatedIntervention: Intervention = {
                    ...item,
                    visitDate: intervention.visitDate,
                    workDate: intervention.workDate,
                    workEndDate: intervention.workEndDate,
                    remark: intervention.remark,
                    satisfaction: intervention.satisfaction,
                    invoiceAmountCents: intervention.invoiceAmountCents,
                    invoiceAmountExclVatCents:
                      intervention.invoiceAmountExclVatCents,
                    invoiceVatType: intervention.invoiceVatType,
                    quoteAmountCents: intervention.quoteAmountCents,
                    quoteAmountExclVatCents:
                      intervention.quoteAmountExclVatCents,
                    quoteVatType: intervention.quoteVatType,
                  };
                  return updatedIntervention;
                }

                return item;
              }),
            })),
          };
        },
      );

      queryClient.invalidateQueries({
        queryKey: queryKeys.interventions.detail(interventionId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.serviceRequests.interventions(serviceRequestId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.serviceRequests.detail(serviceRequestId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.serviceRequests.timeline(serviceRequestId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.serviceRequests.all,
      });

      invalidateSmartEventTimeline({
        id: serviceRequestId,
        queryClient,
        type: ESmartEventTimelineKeyType.ServiceRequest,
      });

      navigate({
        to: '/service-requests/$id',
        params: { id: serviceRequestId },
        replace: true,
      });
      toast.success('Success', {
        description: 'Intervention updated successfully.',
      });
    },
  });

  const handleSubmit = form.handleSubmit(values => {
    updateIntervention({
      id: interventionId,
      remark: values.remark,
      invoiceAmount: values.invoiceAmount,
      invoiceAmountExclVat: values.invoiceAmountExclVat,
      invoiceVatType: values.invoiceVatType,
      quoteAmount: values.quoteAmount,
      quoteAmountExclVat: values.quoteAmountExclVat,
      quoteVatType: values.quoteVatType,
      satisfaction: values.satisfaction,
      visitDate: toDatePayload({
        isVisible: hasVisitFields,
        next: values.visitDate,
        previous: data.intervention.visitDate,
      }),
      workDate: toDatePayload({
        isVisible: hasWorkDates,
        next: values.workDate,
        previous: data.intervention.workDate,
      }),
      workEndDate: toDatePayload({
        isVisible: hasWorkDates,
        next: values.workEndDate,
        previous: data.intervention.workEndDate,
      }),
    });
  });

  return (
    <Form.Provider {...form}>
      <PageTitle
        hasBackButton
        params={{ id: serviceRequestId }}
        title="Update intervention"
        to={'/service-requests/$id'}
      />

      <form
        className="mx-auto mt-8 grid w-full space-y-2 xl:w-4/5 2xl:w-4/6"
        onSubmit={handleSubmit}
      >
        <div className="grid grid-cols-2 gap-x-2">
          <InputControlled
            defaultValue={data.intervention.service.translations.en}
            disabled
            label="Service"
            name="service"
          />

          <InputControlled
            defaultValue={
              data.intervention.provider
                ? data.intervention.provider.companyName ||
                  formatFullName({
                    firstName: data.intervention.provider.firstName,
                    lastName: data.intervention.provider.lastName,
                  })
                : 'No provider assigned'
            }
            disabled
            label="Provider"
            name="provider"
          />
        </div>

        <InputControlled
          label="Remark"
          maxLength={1000}
          name="remark"
          rows={8}
          showCount
          variant="textarea"
        />

        <InterventionScheduleFields
          hasInvoiceAmount={hasInvoiceAmount && !hasInvoiceVatFields}
          hasQuoteAmount={hasQuoteAmount && !isQuotedWork}
          hasVisitFields={hasVisitFields}
          hasWorkDates={hasWorkDates}
          mode="update"
        />

        {isQuotedWork && (
          <>
            <Separator variant="form" />
            <fieldset className="grid h-fit items-start gap-2 sm:grid-cols-3 [&_[data-slot=form-message]]:static">
              <legend className="sr-only">Quote</legend>
              <InputControlled
                label="Quote (incl. VAT)"
                name="quoteAmount"
                prefix="€"
                variant="money"
              />
              <InputControlled
                label="Quote (excl. VAT)"
                name="quoteAmountExclVat"
                prefix="€"
                variant="money"
              />
              <InputControlled
                label="VAT rate"
                name="quoteVatType"
                options={interventionVatTypeOptions}
                placeholder="Select VAT rate"
                variant="select"
              />
            </fieldset>
          </>
        )}

        {hasInvoiceVatFields && (
          <>
            <Separator variant="form" />
            <fieldset className="grid h-fit items-start gap-2 sm:grid-cols-3 [&_[data-slot=form-message]]:static">
              <legend className="sr-only">Invoice</legend>
              <InputControlled
                label="Invoice (incl. VAT)"
                name="invoiceAmount"
                prefix="€"
                variant="money"
              />
              <InputControlled
                label="Invoice (excl. VAT)"
                name="invoiceAmountExclVat"
                prefix="€"
                variant="money"
              />
              <InputControlled
                label="VAT rate"
                name="invoiceVatType"
                options={interventionVatTypeOptions}
                placeholder="Select VAT rate"
                variant="select"
              />
            </fieldset>
          </>
        )}

        <Separator variant="form" />

        <InputControlled
          label="Satisfaction"
          name="satisfaction"
          options={satisfactionOptions}
          variant="select"
        />

        <div className="mt-10 flex justify-center gap-x-2">
          <Anchor
            className="min-w-40 uppercase"
            disabled={isPending}
            params={{ id: serviceRequestId }}
            rounded="lg"
            size="lg"
            to={'/service-requests/$id'}
            variant="destructive"
          >
            Cancel
          </Anchor>
          <Button
            className="min-w-52"
            isLoading={isPending}
            size="lg"
            type="submit"
          >
            Save
          </Button>
        </div>
      </form>
    </Form.Provider>
  );
}
